// Client-side utility: compose sticker image with phrase overlay onto a canvas
// Returns a data URL PNG.

export async function composeStickerWithPhrase(imageBase64, phrase) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const size = 1024;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");

        // Off-white sticker paper background
        ctx.fillStyle = "#FDFBF7";
        ctx.fillRect(0, 0, size, size);

        // Draw the AI image centered, contain-fit
        const scale = Math.min(size / img.width, size / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        const dx = (size - w) / 2;
        const dy = (size - h) / 2;
        ctx.drawImage(img, dx, dy, w, h);

        // Outer thick black border (die-cut sticker look)
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 18;
        ctx.strokeRect(9, 9, size - 18, size - 18);

        // Inner white margin frame
        ctx.strokeStyle = "#FFFFFF";
        ctx.lineWidth = 6;
        ctx.strokeRect(30, 30, size - 60, size - 60);

        // Phrase banner at the bottom (Mexican Green)
        drawPhraseBanner(ctx, phrase, size);

        // Top-right accent badge
        drawWorldCupBadge(ctx, size);

        resolve(canvas.toDataURL("image/png"));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = (e) => reject(e);
    const src = imageBase64.startsWith("data:")
      ? imageBase64
      : `data:image/png;base64,${imageBase64}`;
    img.src = src;
  });
}

function drawPhraseBanner(ctx, phrase, size) {
  const bannerHeight = 180;
  const y = size - bannerHeight - 20;

  // Yellow bar underlay for depth
  ctx.fillStyle = "#FFD100";
  ctx.fillRect(30, y - 12, size - 60, bannerHeight);
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 6;
  ctx.strokeRect(30, y - 12, size - 60, bannerHeight);

  // Green banner
  ctx.fillStyle = "#006847";
  ctx.fillRect(20, y, size - 40, bannerHeight);
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 8;
  ctx.strokeRect(20, y, size - 40, bannerHeight);

  // Phrase text
  ctx.fillStyle = "#FFFFFF";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "bold 76px Anton, Impact, sans-serif";
  wrapText(ctx, phrase, size / 2, y + bannerHeight / 2, size - 100, 78);
}

function drawWorldCupBadge(ctx, size) {
  const cx = size - 130;
  const cy = 130;
  const r = 90;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = "#CE1126";
  ctx.fill();
  ctx.lineWidth = 8;
  ctx.strokeStyle = "#000";
  ctx.stroke();

  ctx.fillStyle = "#FFFFFF";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "bold 30px Anton, Impact, sans-serif";
  ctx.fillText("MUNDIAL", cx, cy - 14);
  ctx.font = "bold 46px Anton, Impact, sans-serif";
  ctx.fillText("2026", cx, cy + 22);
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    const { width } = ctx.measureText(test);
    if (width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);

  const total = lines.length;
  const startY = y - ((total - 1) * lineHeight) / 2;
  lines.forEach((l, i) => {
    // outline
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 6;
    ctx.strokeText(l, x, startY + i * lineHeight);
    ctx.fillText(l, x, startY + i * lineHeight);
  });
}

export function downloadDataUrl(dataUrl, filename = "caliche-sticker.png") {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export async function shareSticker(dataUrl, phrase) {
  try {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const file = new File([blob], "caliche-sticker.png", { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: "Mi Caliche Mundial Sticker",
        text: `${phrase} — Sácate el tuyo en Caliche Mundial Stickers`,
      });
      return { ok: true };
    }
    return { ok: false, reason: "share-unavailable" };
  } catch (e) {
    return { ok: false, reason: String(e) };
  }
}
